import {track, type EventAction} from 'iq-tracking';

// The whole onboarding/signup wizard is a single `signup` event. The step lives
// in `content_type` (indexed for funnel queries), the specific action in
// `event_trigger`, and the entity slug in `content_identifier` on add/remove.
export type SignupStep = 'resume' | 'positions' | 'companies' | 'summary' | 'account' | 'recovery';

export const trackSignup = (args: {
  step: SignupStep;
  trigger: string;
  action: EventAction;
  slug?: string;
  extra?: Record<string, unknown>;
  keepalive?: boolean;
}): Promise<void> =>
  track({
    event: 'signup',
    action: args.action,
    content_type: args.step,
    content_identifier: args.slug,
    trigger: args.trigger,
    extra: args.extra ? {extra_data: JSON.stringify(args.extra)} : undefined,
    keepalive: args.keepalive,
  });

// --- Step views -----------------------------------------------------------

export const trackSignupStepViewed = (
  step: SignupStep,
  stepIndex: number,
  hasResume: boolean,
): Promise<void> =>
  trackSignup({
    step,
    trigger: 'step_viewed',
    action: 'view',
    extra: {step_index: stepIndex, has_resume: hasResume},
  });

// --- Resume step ----------------------------------------------------------

export const trackResumeSelected = (sizeBytes: number, ext: string): Promise<void> =>
  trackSignup({
    step: 'resume',
    trigger: 'resume_selected',
    action: 'click',
    extra: {size_bytes: sizeBytes, ext},
  });

export const trackResumeInvalid = (reason: 'too_large' | 'invalid_pdf'): Promise<void> =>
  trackSignup({step: 'resume', trigger: 'resume_invalid', action: 'click', extra: {reason}});

export const trackResumeUploadStarted = (): Promise<void> =>
  trackSignup({step: 'resume', trigger: 'resume_upload_started', action: 'start'});

export const trackResumeProcessed = (
  suggestedPositions: number,
  suggestedCompanies: number,
  hasContact: boolean,
): Promise<void> =>
  trackSignup({
    step: 'resume',
    trigger: 'resume_processed',
    action: 'execute',
    extra: {
      suggested_positions: suggestedPositions,
      suggested_companies: suggestedCompanies,
      has_contact: hasContact,
    },
  });

export const trackResumeFailed = (
  stage: 'upload' | 'processing' | 'status_check',
  message: string,
): Promise<void> =>
  trackSignup({
    step: 'resume',
    trigger: 'resume_failed',
    action: 'execute',
    extra: {stage, message},
  });

export const trackResumeSkipped = (trigger: 'initial' | 'error'): Promise<void> =>
  trackSignup({step: 'resume', trigger: 'resume_skipped', action: 'click', extra: {trigger}});

// --- Positions step -------------------------------------------------------

export const trackPositionAdded = (
  slug: string,
  name: string,
  suggested: boolean,
): Promise<void> =>
  trackSignup({
    step: 'positions',
    trigger: 'position_added',
    action: 'click',
    slug,
    extra: {name, suggested},
  });

export const trackPositionRemoved = (slug: string, name: string): Promise<void> =>
  trackSignup({
    step: 'positions',
    trigger: 'position_removed',
    action: 'click',
    slug,
    extra: {name},
  });

export const trackPositionsSubmitted = (slugs: string[], suggestedCount: number): Promise<void> =>
  trackSignup({
    step: 'positions',
    trigger: 'positions_submitted',
    action: 'submit',
    extra: {positions: slugs, count: slugs.length, suggested_count: suggestedCount},
  });

// --- Companies step -------------------------------------------------------

export const trackCompanyAdded = (slug: string, name: string): Promise<void> =>
  trackSignup({
    step: 'companies',
    trigger: 'company_added',
    action: 'click',
    slug,
    extra: {name},
  });

export const trackCompanyRemoved = (slug: string, name: string): Promise<void> =>
  trackSignup({
    step: 'companies',
    trigger: 'company_removed',
    action: 'click',
    slug,
    extra: {name},
  });

export const trackCompaniesSubmitted = (slugs: string[]): Promise<void> =>
  trackSignup({
    step: 'companies',
    trigger: 'companies_submitted',
    action: 'submit',
    extra: {companies: slugs, count: slugs.length},
  });

// --- Summary step ---------------------------------------------------------

export const trackSummaryContinued = (
  positionSlugs: string[],
  companySlugs: string[],
): Promise<void> =>
  trackSignup({
    step: 'summary',
    trigger: 'summary_continued',
    action: 'submit',
    extra: {positions: positionSlugs, companies: companySlugs},
  });

export const trackSummaryReview = (): Promise<void> =>
  trackSignup({step: 'summary', trigger: 'summary_review', action: 'click'});
